import { IWorkLogTotal } from '@models';
import { OrderService, OrderWorkLog } from '@services';
import { useMasterBuildAggregate } from './useMasterBuildAggregate';
import {
  summariseWorkLogs,
  sumWorkLogsBy,
  useWorkLogClock,
} from '../ServiceOrder/useOrderWorkLogs';

export interface IMasterBuildWorkLogsHook {
  loading: boolean;
  dataSource: OrderWorkLog[];
  hasOpen: boolean;
  totalMinutes: number;
  minutesByTechnician: IWorkLogTotal<number>[];
  minutesByJob: IWorkLogTotal<string>[];
}

/**
 * Every work-log entry on every leg of a build, read-only. Held in the hook rather than the
 * order store: the store's map is the *job's* list, and a job drawer opened over the build page
 * would otherwise replace this one. Entries are edited on their own job, so nothing writes here.
 */
export const useMasterBuildWorkLogs = (
  masterBuildId: string | undefined,
  enabled: boolean
): IMasterBuildWorkLogsHook => {
  const { loading, dataSource } = useMasterBuildAggregate<OrderWorkLog>(
    enabled && !!masterBuildId,
    (skipCount, maxResultCount) =>
      OrderService.getWorkLogs({ masterBuildId: masterBuildId!, skipCount, maxResultCount }),
    [masterBuildId]
  );

  const hasOpen = dataSource.some((log) => !log.workedTo);
  const now = useWorkLogClock(hasOpen);

  return {
    loading,
    dataSource,
    hasOpen,
    ...summariseWorkLogs(dataSource, now),
    minutesByJob: sumWorkLogsBy(
      dataSource,
      now,
      (log) => log.orderId,
      (log) => log.orderTitle ?? `#${log.orderId.slice(0, 8)}`
    ),
  };
};
